import { zodResolver } from '@hookform/resolvers/zod';
import { createTransaction } from 'api/transaction';
import Button from 'components/form/Button';
import { InputWithoutControl } from 'components/form/Input';
import { NumberInput } from 'components/form/NumberInput';
import SelectInput from 'components/form/SelectInput';
import BackButton from 'components/layouts/BackButton';
import { MessageCard } from 'components/layouts/Comps';
import { NumberDisplay } from 'components/layouts/Display';
import ModalComp from 'components/layouts/Modal';
import { formatNumber, substring } from 'helpers';
import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAccountStore from 'state/accountStore';
import { z } from 'zod';

const WithdrawalSchema = z.object({
  amount: z.string().min(3, 'amount is too small'),
  wallet: z.string().min(1, 'select wallet'),
  agree: z.boolean(),
});

type WithdrawalSchemaType = z.infer<typeof WithdrawalSchema>;

const Withdraw = () => {
  const { account } = useAccountStore();
  const [wallets, setWallets] = useState<
    { value: string; label: string; key: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { isDirty, isValid },
  } = useForm<WithdrawalSchemaType>({
    resolver: zodResolver(WithdrawalSchema),
    defaultValues: {
      agree: false,
    },
  });

  const agree = useWatch({ control, name: 'agree' });

  const makeTransaction = async (data: any) => {
    setLoading(true);
    try {
      const res = await createTransaction(data);

      if (res?.data) {
        reset();
        toast.success('Withdrawal request sent');
        setOpenModal(true);
      }
    } catch (error: any) {
      const { errors } = error.response.data;
      errors.map((err: { error: string }) => {
        if (err?.error) {
          return toast.error(err.error);
        } else {
          return toast.error('Something went wrong');
        }
      });
      console.log(error);
    }
    setLoading(false);
  };

  const onSubmit = (data: WithdrawalSchemaType) => {
    const wallet = wallets.find((item) => item.value === data.wallet);

    // wallet: wallet?.value,
    makeTransaction({
      crypto: wallet?.key,
      amount: formatNumber(data.amount),
      trxType: 'WITHDRAWAL',
      wallet: data.wallet,
    });
  };

  useEffect(() => {
    if (!account) return;
    const options = [];
    for (const [key, value] of Object.entries(account.wallet)) {
      if (value.length)
        options.push({
          value: value,
          label: `${key} - ${substring(value, 35)}`,
          key,
        });
    }
    setWallets(options);
  }, [account]);

  return (
    <section className="py-5">
      <BackButton text="wallet" />
      <br />
      <div>
        <h3 className="my-3 flex items-center gap-x-2 rounded-md bg-slate-100 p-3 text-xl">
          <span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="h-6 w-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
              />
            </svg>
          </span>
          <span> Place a withdrawal request.</span>
        </h3>
        <br />
        <div className="flex flex-col justify-between gap-5 md:flex-row">
          <div className="w-full rounded-xl border border-green-500 bg-green-400 py-8 px-10">
            <div className="flex items-end justify-between">
              <div>
                <p className="font-semibold text-gray-700">Investment</p>
                <br />
                <p className="text-[23px] font-bold">
                  <NumberDisplay value={account?.investment ?? 0} />
                </p>
              </div>
              <Link to="/dashboard/invest">
                <p className="rounded-md bg-brand-dark px-3 py-2 text-white">
                  invest now
                </p>
              </Link>
            </div>
          </div>
          <div className="w-full rounded-xl border border-blue-400 bg-blue-500 py-8 px-10 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-700">Balance</p>
                <br />
                <p className="font-bold">
                  <NumberDisplay value={account?.balance ?? 0} />
                </p>
              </div>
            </div>
          </div>
          <div className="w-full rounded-xl border border-slate-300 bg-slate-200 py-8 px-10">
            <p className="font-semibold text-gray-700">Profit</p>
            <p className="font-bold">
              <NumberDisplay value={account?.profit ?? 0} />
            </p>
          </div>
        </div>
        <br />
        <div className="rounded-md border border-slate-100 bg-white py-8 px-3">
          <div className="mx-auto md:w-[50%]">
            <form className="flex flex-col gap-y-8">
              <div className="flex flex-col gap-y-2">
                <label htmlFor="coin" className="font-semibold">
                  Amount:
                </label>

                <NumberInput
                  control={control}
                  name="amount"
                  placeholder="$12,000"
                  prefix={'$'}
                  decimalScale={2}
                  fixedDecimalScale
                  className="py-3 md:py-1"
                  isAllowed={(values: { floatValue: number }) => {
                    const { floatValue } = values;
                    return floatValue <= +account?.balance!;
                  }}
                />
              </div>
              <div className="flex flex-col gap-y-2">
                <label htmlFor="coin" className="font-semibold">
                  Wallet:
                </label>

                <SelectInput
                  name="wallet"
                  control={control}
                  options={wallets}
                  containerClass="bg-white"
                  label="Select wallet address"
                />
              </div>
              <div>
                <label className="flex cursor-pointer items-center justify-center gap-y-1">
                  <InputWithoutControl
                    name="agree"
                    type="checkbox"
                    containerClass="border-none"
                    handleChange={() => setValue('agree', !agree)}
                    checked={agree}
                  />
                  Agree to terms and conditions
                </label>
              </div>
              <Button
                className="flex w-full items-center justify-center gap-x-3 rounded-md bg-brand-dark !py-2 text-white"
                disabled={!isValid || !isDirty || !agree || loading}
                onClick={handleSubmit(onSubmit)}
              >
                proceed
              </Button>
            </form>
            <br />
            <div className="flex justify-center gap-x-2">
              <span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-6 w-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                  />
                </svg>
              </span>
              <p>secured</p>
            </div>
          </div>
        </div>
      </div>
      {openModal && (
        <ModalComp
          toggleModal={() => setOpenModal(false)}
          width="w-full md:w-[400px]"
        >
          <div className="p-3">
            <MessageCard
              title="Success 🎆🎇"
              text={
                <div>
                  <p>
                    Your request has been received and will be processed
                    immediately, The selected wallet address will receive the
                    requested withdrawal.{' '}
                  </p>
                </div>
              }
            />
            <Button
              className="my-2 w-full rounded-md bg-red-500 p-2 text-white"
              onClick={() => setOpenModal(false)}
            >
              close
            </Button>
          </div>
        </ModalComp>
      )}
    </section>
  );
};

export default Withdraw;
